/**
 * AtRiskSkusTable Component
 *
 * Displays the list of at-risk SKUs in a table format.
 * Default shows 10 rows, expandable to show all.
 */

import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface AtRiskSku {
  sku: string;
  location?: string;
  risk_type?: 'stockout' | 'below_target' | string;
  days_until_risk?: number;
  total_unfulfilled_demand?: number;
  // Severity classification fields
  severity?: 'CRITICAL' | 'WARNING' | 'INFO';
  is_temporary?: boolean;
  recovery_date?: string;
  recovery_quantity?: number;
  stockout_days?: number;
}

interface AtRiskSkusTableProps {
  skus: AtRiskSku[];
}

const DEFAULT_VISIBLE_ROWS = 10;

export const AtRiskSkusTable = ({ skus }: AtRiskSkusTableProps) => {
  const [showAll, setShowAll] = useState(false);

  if (!skus || skus.length === 0) return null;

  const visibleSkus = showAll ? skus : skus.slice(0, DEFAULT_VISIBLE_ROWS);
  const hasMore = skus.length > DEFAULT_VISIBLE_ROWS;

  const getRiskChip = (riskType?: string) => {
    if (!riskType) return null;
    const isStockout = riskType === 'stockout';
    return (
      <Chip
        label={isStockout ? 'Stockout' : 'Below Target'}
        size="small"
        sx={{
          backgroundColor: isStockout ? '#fee2e2' : '#fef3c7',
          color: isStockout ? '#dc2626' : '#d97706',
          fontWeight: 500,
          fontSize: '11px',
          height: '20px',
        }}
      />
    );
  };

  const formatDays = (days?: number) => {
    if (days === undefined || days === null) return '-';
    if (days === 0) return 'NOW';
    return `${days}d`;
  };

  const formatUnfulfilled = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return value.toLocaleString();
  };

  return (
    <Box sx={{ mt: 1 }}>
      <TableContainer>
        <Table size="small" sx={{ '& td, & th': { py: 0.75, px: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '11px', color: '#6b7280' }}>
                SKU
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '11px', color: '#6b7280' }}>
                Location
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '11px', color: '#6b7280' }}>
                Severity
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '11px', color: '#6b7280' }}>
                Risk
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '11px', color: '#6b7280' }}>
                Days
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '11px', color: '#6b7280' }} align="right">
                Unfulfilled
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleSkus.map((sku, index) => (
              <TableRow
                key={`${sku.sku}-${sku.location}-${index}`}
                sx={{ '&:last-child td': { borderBottom: 0 } }}
              >
                <TableCell
                  sx={{
                    fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
                    fontSize: '12px',
                    color: '#1a1a1a',
                  }}
                >
                  {sku.sku}
                </TableCell>
                <TableCell sx={{ fontSize: '12px', color: '#374151' }}>
                  {sku.location ?? '-'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 500,
                        backgroundColor:
                          sku.severity === 'CRITICAL'
                            ? '#fee2e2'
                            : sku.severity === 'WARNING'
                              ? '#fef3c7'
                              : '#dbeafe',
                        color:
                          sku.severity === 'CRITICAL'
                            ? '#dc2626'
                            : sku.severity === 'WARNING'
                              ? '#d97706'
                              : '#2563eb',
                      }}
                    >
                      {sku.severity || 'UNKNOWN'}
                    </span>
                    {sku.is_temporary && sku.recovery_date && (
                      <Typography
                        component="span"
                        sx={{ fontSize: '10px', color: '#6b7280' }}
                      >
                        → {new Date(sku.recovery_date).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{getRiskChip(sku.risk_type)}</TableCell>
                <TableCell
                  sx={{
                    fontSize: '12px',
                    color: sku.days_until_risk === 0 ? '#dc2626' : '#374151',
                    fontWeight: sku.days_until_risk === 0 ? 600 : 400,
                  }}
                >
                  {formatDays(sku.days_until_risk)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
                    fontSize: '12px',
                    color: '#374151',
                  }}
                >
                  {formatUnfulfilled(sku.total_unfulfilled_demand)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {hasMore && (
        <Button
          size="small"
          onClick={() => setShowAll(!showAll)}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          }
          sx={{
            mt: 1,
            textTransform: 'none',
            fontSize: '12px',
            color: '#6b7280',
            '&:hover': { backgroundColor: '#f3f4f6' },
          }}
        >
          {showAll ? 'Show less' : `Show all ${skus.length} SKUs`}
        </Button>
      )}
    </Box>
  );
};
